import Template from "pages/components/Template"
import { useTranslation } from "react-i18next"
import Header from "components/Header"
import { BillingAddress, DateRange, PARTNER_RETRO_COMMISSION_SNAPSHOTS } from "./queries"
import { useQuery } from "@apollo/client"
import Loader, { LoaderContainer } from "components/Loader"
import BillingSpaceSection from "./components/BillingSpaceSection"
import { BillingSpacesContainer, FacturationContent } from "./styles"
import { useMemo, useCallback, useState } from "react"
import { Moment } from "moment"
import { endOfMonth, startOfMonth, subMonths } from "date-fns"
import BillingStateCard from "./components/BillingStateCard"
import BillingPeriodControls from "./components/BillingPeriodControls"
import { Redirect } from "react-router-dom"

const defaultPeriod = (): DateRange => ({
    startDate: startOfMonth(subMonths(new Date(), 5)),
    endDate: endOfMonth(new Date())
})

export default function Facturation() {
    const { t } = useTranslation()
    const [period, setPeriod] = useState<DateRange>(defaultPeriod())

    const variables = useMemo(
        () => ({
            periodFrom: period.startDate.toISOString(),
            periodTo: period.endDate.toISOString()
        }),
        [period.startDate, period.endDate]
    )

    const onPeriodChange = useCallback((value: [Moment | null, Moment | null]) => {
        if (!value[0] || !value[1]) return
        setPeriod({
            startDate: startOfMonth(value[0].toDate()),
            endDate: endOfMonth(value[1].toDate())
        })
    }, [])

    const expandPeriod = useCallback(() => {
        setPeriod({
            startDate: startOfMonth(subMonths(new Date(), 11)),
            endDate: endOfMonth(new Date())
        })
    }, [])

    const { data, previousData, error, loading, refetch } = useQuery(PARTNER_RETRO_COMMISSION_SNAPSHOTS, {
        variables,
        notifyOnNetworkStatusChange: true
    })

    const resolvedData = data ?? previousData
    const isRefetching = loading && !!previousData

    const billingAddresses = useMemo(() => {
        if (!resolvedData?.currentAddresses) return []
        return resolvedData.currentAddresses
            .filter((billingAddress: BillingAddress) => billingAddress.partnerBillingSpace)
            .sort((a: BillingAddress, b: BillingAddress) => a.shortname.localeCompare(b.shortname))
    }, [resolvedData])

    return (
        <Template activeMenu='/facturation'>
            {(currentUser, currentCenters) => {
                if (currentCenters.length !== 1 || !currentCenters[0].partnerBillingSpace) {
                    return <Redirect to="/" />
                }

                return (
                    <>
                        <Header
                            title={t('facturation.title')}
                            currentUser={currentUser}
                            currentCenters={currentCenters}
                        />

                        <FacturationContent>
                            {loading && !isRefetching && (
                                <LoaderContainer><Loader /></LoaderContainer>
                            )}

                            {!loading && error && (
                                <BillingStateCard
                                    title={t('facturation.error')}
                                    description={t('facturation.error-hint')}
                                    actionLabel={t('facturation.retry')}
                                    onAction={refetch}
                                />
                            )}

                            {!loading && !error && billingAddresses.length === 0 && (
                                <BillingStateCard
                                    title={t('facturation.no-billing-spaces')}
                                    description={t('facturation.no-billing-spaces-hint')}
                                />
                            )}

                            {(isRefetching || (!loading && !error && billingAddresses.length > 0)) && (
                                <BillingSpacesContainer $loading={isRefetching}>
                                    <BillingPeriodControls
                                        period={period}
                                        onPeriodChange={onPeriodChange}
                                    />

                                    {billingAddresses.map((billingAddress: BillingAddress) => (
                                        <BillingSpaceSection
                                            key={billingAddress.id}
                                            billingAddress={billingAddress}
                                            period={period}
                                            onExpandPeriod={expandPeriod}
                                        />
                                    ))}
                                </BillingSpacesContainer>
                            )}
                        </FacturationContent>
                    </>
                )
            }}
        </Template>
    )
}
